import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import emailService from '../services/emailService';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';
    import { useData } from '../context/DataContext';

    const { FiX, FiSave, FiAlertTriangle, FiLock, FiSend, FiCheck } = FiIcons;

    const SmtpSettingsModal = ({ onClose }) => {
      const { appSettings } = useData();
      const [formData, setFormData] = useState({ host: '', port: 587, secure: false, user: '', password: '', fromName: '', fromEmail: '', });
      const [loading, setLoading] = useState(false);
      const [testing, setTesting] = useState(false);
      const [testResult, setTestResult] = useState(null);
      const [testEmail, setTestEmail] = useState('');

      useEffect(() => {
        loadExistingConfig();
      }, []);

      const loadExistingConfig = async () => {
        const config = await emailService.getSmtpConfig();
        if (config) {
          setFormData(prev => ({ ...prev, ...config }));
        }
      };

      const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
      };

      const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
          const result = await emailService.saveSmtpConfig(formData);
          if (result.success) {
            alert('SMTP configuration saved successfully!');
            await emailService.initialize();
            onClose();
          } else {
            alert(`Error: ${result.message}`);
          }
        } catch (error) {
          console.error('Error saving SMTP config:', error);
          alert('Failed to save SMTP configuration');
        } finally {
          setLoading(false);
        }
      };

      const handleTestEmail = async () => {
        if (!testEmail) {
          alert('Please enter an email address to send the test to.');
          return;
        }
        setTesting(true);
        setTestResult(null);
        try {
          const saveResult = await emailService.saveSmtpConfig(formData);
          if (!saveResult.success) {
            throw new Error(saveResult.message);
          }
          await emailService.initialize();
          const result = await emailService.sendTestEmail(testEmail, appSettings);
          if (result.success) {
            setTestResult({ success: true, message: `Test email has been queued for sending to ${testEmail}. Check your inbox.` });
          } else {
            setTestResult({ success: false, message: result.message });
          }
        } catch (error) {
          console.error('Error sending test email:', error);
          setTestResult({ success: false, message: error.message });
        } finally {
          setTesting(false);
        }
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Email Settings (SMTP)</h2>
                <p className="text-sm text-gray-600 mt-1">Configure backend email sending via SMTP.</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-900">Security Warning</h4>
                    <p className="text-sm text-yellow-800 mt-1">
                      Your SMTP password will be stored in your Firestore database. It is critical to secure your <strong>emailConfig</strong> collection using Firestore Security Rules to prevent unauthorized access. Only administrators should have read/write permissions.
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">SMTP Server Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host *</label>
                    <input type="text" value={formData.host} onChange={(e) => handleChange('host', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., smtp.example.com" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port *</label>
                    <input type="number" value={formData.port} onChange={(e) => handleChange('port', parseInt(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., 587" required />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-3">
                      <input type="checkbox" checked={formData.secure} onChange={(e) => handleChange('secure', e.target.checked)} className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                      <span className="text-sm text-gray-700">Use SSL/TLS (Secure Connection)</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Usually checked for port 465.</p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">SMTP Authentication</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP User *</label>
                    <input type="text" value={formData.user} onChange={(e) => handleChange('user', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., user@example.com" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password *</label>
                    <input type="password" value={formData.password} onChange={(e) => handleChange('password', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter SMTP password" required />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Sender Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Name *</label>
                    <input type="text" value={formData.fromName} onChange={(e) => handleChange('fromName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., ExpertGuru" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Email *</label>
                    <input type="email" value={formData.fromEmail} onChange={(e) => handleChange('fromEmail', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., no-reply@expertguru.com" required />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Test Configuration</h4>
                <div className="flex gap-3">
                  <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" placeholder="Enter email to send test to" />
                  <button type="button" onClick={handleTestEmail} disabled={testing || !testEmail} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                    {testing ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Testing...</>
                    ) : (
                      <><SafeIcon icon={FiSend} /> Send Test</>
                    )}
                  </button>
                </div>
                {testResult && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className={`mt-3 p-3 rounded-lg border ${testResult.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={testResult.success ? FiCheck : FiAlertTriangle} />
                      <span className="text-sm font-medium">{testResult.success ? 'Success!' : 'Failed!'}</span>
                    </div>
                    <p className="text-sm mt-1">{testResult.message}</p>
                  </motion.div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <motion.button whileHover={{ scale: loading ? 1 : 1.05 }} whileTap={{ scale: loading ? 1 : 0.95 }} type="submit" disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {loading ? 'Saving...' : <><SafeIcon icon={FiSave} /> Save Configuration</>}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      );
    };
    export default SmtpSettingsModal;